import { ModuleIntro } from "@/components/ModuleIntro";
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Layout, Brand } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { usePatientMode } from "@/lib/patientMode";
import {
  CONNAIS_PARTS, CONNAIS_QUESTIONS, CONNAIS_MAX, CONNAIS_BONUS,
  CONNAIS_DISCOVERY, CONNAIS_APPORTER, CONNAIS_T, curiosityReading,
} from "@/data/connais";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const fallback = (n, d) => (n && n.trim() ? n.trim() : d);

const Textarea = (props) => (
  <textarea rows={2} {...props} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
);
const Primary = ({ children, ...p }) => (
  <button {...p} className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{children}</button>
);
const Ghost = ({ children, ...p }) => (
  <button {...p} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{children}</button>
);

const getA = (answers, n) => (answers && answers[n]) || {};

/* ---------- Fill sheet ---------- */
const FillSheet = ({ t, lang, name, otherName, answers, onChange, onPrev, onNext, nextLabel }) => (
  <div className="animate-fade-up">
    <div className="rounded-3xl bg-[var(--gold)] text-white p-5 sm:p-6 mb-6 text-center">
      <p className="text-xs uppercase tracking-[0.2em] text-white/80 mb-1">{t.sheetOf}</p>
      <h2 className="font-serif-title text-3xl">{name}</h2>
      <p className="text-sm text-white/90 mt-2">{t.sheetHint(otherName)}</p>
    </div>
    <div className="space-y-8">
      {CONNAIS_PARTS.map((part) => (
        <div key={part.id}>
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{part.title[lang]}</h3>
          {part.note && <p className="text-xs text-[var(--ink-soft)] italic mb-3">{part.note[lang]}</p>}
          <div className="space-y-4">
            {part.questions.map((qq) => {
              const a = getA(answers, qq.n);
              return (
                <div key={qq.n} className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4" data-testid={`connais-q-${qq.n}`}>
                  <p className="text-sm font-semibold text-[var(--ink)] mb-1"><span className="text-[var(--gold)]">{qq.n}.</span> {qq.q[lang]}</p>
                  {qq.type === "choice" && (
                    <p className="text-[0.7rem] text-[var(--ink-soft)] mb-2">{t.optionsHint} {qq.options[lang].join(" · ")}</p>
                  )}
                  {qq.type === "table" ? (
                    <div className="space-y-1.5 mt-2">
                      <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[0.7rem] text-[var(--ink-soft)] font-semibold">
                        <span></span><span className="w-14 text-center">{t.tableGuess}</span><span className="w-14 text-center">{t.tableReality}</span>
                      </div>
                      {qq.rows[lang].map((row, ri) => (
                        <div key={ri} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                          <span className="text-xs text-[var(--ink)]">{row}</span>
                          <input type="number" min="0" max="10" value={(a.gt && a.gt[ri]) ?? ""} onChange={(e) => onChange(qq.n, "gt", { ...(a.gt || {}), [ri]: e.target.value })} data-testid={`connais-${qq.n}-gt-${ri}`} className="w-14 rounded-lg border border-[var(--border)] bg-white/80 p-1.5 text-center text-sm" />
                          <input type="number" min="0" max="10" value={(a.rt && a.rt[ri]) ?? ""} onChange={(e) => onChange(qq.n, "rt", { ...(a.rt || {}), [ri]: e.target.value })} data-testid={`connais-${qq.n}-rt-${ri}`} className="w-14 rounded-lg border border-[var(--border)] bg-white/80 p-1.5 text-center text-sm" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-[var(--ink-soft)] mb-1">{t.guessLabel(otherName)}</label>
                        <Textarea value={a.g || ""} onChange={(e) => onChange(qq.n, "g", e.target.value)} data-testid={`connais-${qq.n}-g`} placeholder={t.guessPh} />
                      </div>
                      <div>
                        <label className="block text-xs text-[var(--ink-soft)] mb-1">{t.realityLabel}</label>
                        <Textarea value={a.r || ""} onChange={(e) => onChange(qq.n, "r", e.target.value)} data-testid={`connais-${qq.n}-r`} placeholder={t.realityPh} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between mt-8 no-print">
      <Ghost onClick={onPrev}>{t.prev}</Ghost>
      <Primary onClick={onNext} data-testid="connais-fill-next">{nextLabel}</Primary>
    </div>
  </div>
);

/* ---------- Compare + score ---------- */
const MARK_VALUES = { good: 2, close: 1, wrong: 0 };

const CompareRow = ({ t, subjectName, observerName, guessText, realityText, value, onMark, testid }) => (
  <div className="rounded-xl bg-white/60 border border-[var(--border)] p-3" data-testid={testid}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
      <div className="rounded-lg bg-[var(--sand)] p-2">
        <p className="text-[0.7rem] font-semibold text-[var(--ink)] mb-0.5">{t.guessedBy(observerName)}</p>
        <p className="text-sm text-[var(--ink-soft)] whitespace-pre-wrap">{guessText || "—"}</p>
      </div>
      <div className="rounded-lg bg-[var(--sand)] p-2">
        <p className="text-[0.7rem] font-semibold text-[var(--ink)] mb-0.5">{t.realityOf(subjectName)}</p>
        <p className="text-sm text-[var(--ink-soft)] whitespace-pre-wrap">{realityText || "—"}</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 no-print">
      {[["good", t.markGood], ["close", t.markClose], ["wrong", t.markWrong]].map(([k, lbl]) => (
        <button key={k} onClick={() => onMark(k)} data-testid={`${testid}-${k}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${value === k ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--gold)]"}`}>
          {lbl}
        </button>
      ))}
    </div>
    <div className="hidden print:block text-xs text-[var(--ink-soft)] mt-1">{value ? { good: t.markGood, close: t.markClose, wrong: t.markWrong }[value] : "—"}</div>
  </div>
);

const tableText = (a, key, rows, lang) => {
  const o = a[key] || {};
  return rows[lang].map((r, i) => `${r}: ${o[i] ?? "—"}/10`).join("  ·  ");
};

const Compare = ({ t, lang, a, b, marks, setMark, extra, setExtra, onRestart }) => {
  const nameA = fallback(a.name, t.partner1);
  const nameB = fallback(b.name, t.partner2);
  const scoreA = CONNAIS_QUESTIONS.reduce((s, q) => s + (MARK_VALUES[marks.a[q.n]] || 0), 0);
  const scoreB = CONNAIS_QUESTIONS.reduce((s, q) => s + (MARK_VALUES[marks.b[q.n]] || 0), 0);
  const gtxt = (ans, q) => (q.type === "table" ? tableText(getA(ans.answers, q.n), "gt", q.rows, lang) : getA(ans.answers, q.n).g);
  const rtxt = (ans, q) => (q.type === "table" ? tableText(getA(ans.answers, q.n), "rt", q.rows, lang) : getA(ans.answers, q.n).r);
  const setEx = (k, v) => setExtra((p) => ({ ...p, [k]: v }));

  return (
    <div className="animate-fade-up print-container" data-testid="connais-compare">
      <div className="hidden print:block mb-6"><Brand /></div>
      <div className="text-center mb-8">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{t.compareTitle}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.compareText}</p>
      </div>

      {/* Score */}
      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 mb-8 text-center print-break" data-testid="connais-score">
        <h3 className="font-serif-title text-2xl mb-3">{t.scoreTitle}</h3>
        <div className="flex justify-center gap-8 mb-3">
          <div><p className="text-xs uppercase tracking-wide text-white/80">{t.scoreOf(nameA)}</p><p className="font-serif-title text-4xl" data-testid="connais-score-a">{scoreA}<span className="text-lg">/{CONNAIS_MAX}</span></p></div>
          <div><p className="text-xs uppercase tracking-wide text-white/80">{t.scoreOf(nameB)}</p><p className="font-serif-title text-4xl" data-testid="connais-score-b">{scoreB}<span className="text-lg">/{CONNAIS_MAX}</span></p></div>
        </div>
        <p className="text-xs text-white/85 max-w-md mx-auto leading-relaxed">{t.scoreNote}</p>
      </div>

      {/* Bilan de curiosité guidé */}
      <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-5 sm:p-6 mb-8 print-break" data-testid="connais-reading">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gold)] mb-3">{t.readingTitle}</p>
        <div className="space-y-2">
          {curiosityReading(scoreA, scoreB, CONNAIS_MAX, lang).map((line, i) => (
            <p key={i} className="text-sm text-[var(--ink)] leading-relaxed">{line}</p>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {CONNAIS_PARTS.map((part) => (
          <div key={part.id} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
            <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{part.title[lang]}</h3>
            <div className="space-y-5">
              {part.questions.map((q) => (
                <div key={q.n}>
                  <p className="text-sm font-semibold text-[var(--ink)] mb-2"><span className="text-[var(--gold)]">{q.n}.</span> {q.q[lang]}</p>
                  <div className="space-y-3">
                    <CompareRow t={t} subjectName={nameB} observerName={nameA} guessText={gtxt(a, q)} realityText={rtxt(b, q)} value={marks.a[q.n]} onMark={(k) => setMark("a", q.n, k)} testid={`connais-cmp-a-${q.n}`} />
                    <CompareRow t={t} subjectName={nameA} observerName={nameB} guessText={gtxt(b, q)} realityText={rtxt(a, q)} value={marks.b[q.n]} onMark={(k) => setMark("b", q.n, k)} testid={`connais-cmp-b-${q.n}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Bonus */}
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.bonusTitle}</h3>
          <p className="text-sm text-[var(--ink)] mb-3">{CONNAIS_BONUS.q[lang]}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Textarea value={extra.bonusG || ""} onChange={(e) => setEx("bonusG", e.target.value)} data-testid="connais-bonus-g" placeholder={t.guessPh} />
            <Textarea value={extra.bonusR || ""} onChange={(e) => setEx("bonusR", e.target.value)} data-testid="connais-bonus-r" placeholder={t.realityPh} />
          </div>
        </div>

        {/* Découverte */}
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{t.discoveryTitle}</h3>
          <div className="space-y-3">
            {CONNAIS_DISCOVERY.map((d) => (
              <div key={d.key}>
                <label className="block text-sm font-semibold text-[var(--ink)] mb-1">{d.label[lang]}</label>
                <Textarea value={extra[d.key] || ""} onChange={(e) => setEx(d.key, e.target.value)} data-testid={`connais-${d.key}`} placeholder={t.answerPh} />
              </div>
            ))}
          </div>
        </div>

        {/* Micro-défi */}
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{t.challengeTitle}</h3>
          <label className="block text-sm font-semibold text-[var(--ink)] mb-1">{t.challengeAction}</label>
          <Textarea value={extra.challenge || ""} onChange={(e) => setEx("challenge", e.target.value)} data-testid="connais-challenge" placeholder={t.answerPh} />
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="text-sm font-semibold text-[var(--ink)] self-center">{t.challengeWho}</span>
            {[["me", t.whoMe], ["you", t.whoYou], ["both", t.whoBoth]].map(([k, lbl]) => (
              <button key={k} onClick={() => setEx("who", k)} data-testid={`connais-who-${k}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${extra.who === k ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--gold)]"}`}>{lbl}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div><label className="block text-sm font-semibold text-[var(--ink)] mb-1">{t.challengeWhen}</label><Textarea value={extra.when || ""} onChange={(e) => setEx("when", e.target.value)} data-testid="connais-when" placeholder={t.answerPh} /></div>
            <div><label className="block text-sm font-semibold text-[var(--ink)] mb-1">{t.challengeHow}</label><Textarea value={extra.how || ""} onChange={(e) => setEx("how", e.target.value)} data-testid="connais-how" placeholder={t.answerPh} /></div>
          </div>
        </div>

        {/* Phrase de fin */}
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.endPhraseTitle}</h3>
          <p className="text-sm italic text-[var(--ink)] mb-3">{t.endPhrase}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Textarea value={extra.endG || ""} onChange={(e) => setEx("endG", e.target.value)} data-testid="connais-end-g" placeholder={t.guessPh} />
            <Textarea value={extra.endR || ""} onChange={(e) => setEx("endR", e.target.value)} data-testid="connais-end-r" placeholder={t.realityPh} />
          </div>
        </div>

        {/* À apporter */}
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-6 print-break">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{t.apporterTitle}</h3>
          <p className="text-sm text-[var(--ink-soft)] mb-4">{t.apporterIntro}</p>
          <div className="space-y-3">
            {CONNAIS_APPORTER.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-semibold text-[var(--ink)] mb-1">{f.label[lang]}</label>
                <Textarea value={extra[f.key] || ""} onChange={(e) => setEx(f.key, e.target.value)} data-testid={`connais-${f.key}`} placeholder={t.answerPh} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 mt-8 no-print">
        <Primary onClick={() => window.print()} data-testid="connais-btn-export-pdf">{t.exportPdf}</Primary>
        <Ghost onClick={onRestart} data-testid="connais-btn-restart">{t.restart}</Ghost>
      </div>
    </div>
  );
};

/* ---------- Module ---------- */
export default function ConnaisModule({ initialLang }) {
  const { patientMode } = usePatientMode();
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState("welcome");
  const [mode, setMode] = useState("single");
  const [p1Name, setP1Name] = useState("");
  const [p2Name, setP2Name] = useState("");
  const [p1Answers, setP1Answers] = useState({});
  const [p2Answers, setP2Answers] = useState({});
  const [role, setRole] = useState("p1");
  const [code, setCode] = useState("");
  const [myName, setMyName] = useState("");
  const [myAnswers, setMyAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [marks, setMarks] = useState({ a: {}, b: {} });
  const [extra, setExtra] = useState({});
  const [searchParams] = useSearchParams();
  const t = CONNAIS_T[lang];

  useEffect(() => {
    const c = searchParams.get("code");
    if (c) { setMode("two"); setRole("p2"); setCode(c.toUpperCase()); setPhase("td_join"); }
  }, []); // eslint-disable-line

  const n1 = fallback(p1Name, t.partner1);
  const n2 = fallback(p2Name, t.partner2);
  const go = (p) => { setPhase(p); scrollTop(); };
  const setMark = (dir, n, v) => setMarks((prev) => ({ ...prev, [dir]: { ...prev[dir], [n]: v } }));
  const chg = (setter) => (n, key, v) => setter((prev) => ({ ...prev, [n]: { ...(prev[n] || {}), [key]: v } }));

  const finishSingle = () => { setResult({ a: { name: n1, answers: p1Answers }, b: { name: n2, answers: p2Answers } }); go("compare"); };

  const createSeance = async () => {
    try { const { code: c } = await axios.post(`${API}/connais/seances`).then((r) => r.data); setCode(c); setRole("p1"); go("td_code"); }
    catch { toast.error(t.createFail); }
  };
  const joinSeance = async () => {
    if (code.trim().length < 4) { toast.error(t.enterValidCode); return; }
    try { const d = await axios.get(`${API}/connais/seances/${code.trim()}`).then((r) => r.data); if (!d.found) { toast.error(t.codeNotFound); return; } setRole("p2"); go("td_fill"); }
    catch { toast.error(t.joinError); }
  };
  const resumeSeance = async () => {
    if (code.trim().length < 4) { toast.error(t.enterValidCode); return; }
    try {
      const d = await axios.get(`${API}/connais/seances/${code.trim()}`).then((r) => r.data);
      if (!d.found) { toast.error(t.codeNotFound); return; }
      if (!d.both_submitted) { toast.error(t.resumeNotReady); return; }
      setResult({ a: d.p1, b: d.p2 }); go("compare");
    } catch { toast.error(t.joinError); }
  };
  const submitTwo = async () => {
    try {
      const out = await axios.post(`${API}/connais/seances/${code}/submit`, { role, name: myName, answers: myAnswers }).then((r) => r.data);
      if (out.both_submitted) { setResult({ a: out.p1, b: out.p2 }); go("compare"); } else go("td_wait");
    } catch { toast.error(t.submitFail); }
  };

  const restart = () => {
    setP1Name(""); setP2Name(""); setP1Answers({}); setP2Answers({}); setRole("p1"); setCode(""); setMyName(""); setMyAnswers({});
    setResult(null); setMarks({ a: {}, b: {} }); setExtra({}); setMode("single"); go("welcome");
  };

  const central = result || { a: { name: n1, answers: p1Answers }, b: { name: n2, answers: p2Answers } };

  const render = () => {
    switch (phase) {
      case "welcome":
        return (
          <div className="animate-fade-up text-center">
            <img src="/modules/connais.jpg" alt="" className="w-full h-52 sm:h-64 object-cover rounded-3xl mb-8" data-testid="connais-welcome-hero" />
            <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] leading-tight mb-4">{t.welcomeTitle}</h1>
            <p className="text-[var(--ink-soft)] max-w-lg mx-auto leading-relaxed mb-8">{t.subtitle}</p>
            <ModuleIntro moduleKey="connais" lang={lang} className="mb-8" />
            <Primary onClick={() => go("rules")} data-testid="connais-btn-start">{t.start}</Primary>
          </div>
        );
      case "rules":
        return (
          <div className="animate-fade-up">
            <img src="/couple/connais-inside.jpg" alt="" className="w-full h-40 sm:h-52 object-cover rounded-3xl mb-6" data-testid="connais-rules-photo" />
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.rulesTitle}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 mb-4 space-y-3">
              {t.rules.map((r, i) => (<p key={i} className="text-[var(--ink)] leading-relaxed">{r}</p>))}
            </div>
            <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-5 mb-6">
              <p className="font-semibold text-[var(--gold)] mb-1">{t.rule1Title}</p>
              <p className="text-sm text-[var(--ink)] leading-relaxed">{t.rule1Text}</p>
            </div>
            <div className="flex items-center justify-between">
              <Ghost onClick={() => go("welcome")}>{t.prev}</Ghost>
              <Primary onClick={() => go("mode")} data-testid="connais-rules-next">{t.next}</Primary>
            </div>
          </div>
        );
      case "mode":
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.modeTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <button onClick={() => { setMode("single"); go("setup"); }} data-testid="connais-mode-single" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeSingle}</h3><p className="text-sm text-[var(--ink-soft)]">{t.modeSingleDesc}</p>
              </button>
              <button onClick={() => { setMode("two"); go("td_role"); }} data-testid="connais-mode-two" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeTwo}</h3><p className="text-sm text-[var(--ink-soft)]">{t.modeTwoDesc}</p>
              </button>
            </div>
            <div className="mt-8"><Ghost onClick={() => go("rules")}>{t.prev}</Ghost></div>
          </div>
        );
      case "setup":
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.setupTitle}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 space-y-5">
              <div><label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.partner1}</label><input value={p1Name} onChange={(e) => setP1Name(e.target.value)} data-testid="connais-p1-name" placeholder={t.partner1Ph} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm" /></div>
              <div><label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.partner2}</label><input value={p2Name} onChange={(e) => setP2Name(e.target.value)} data-testid="connais-p2-name" placeholder={t.partner2Ph} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm" /></div>
            </div>
            <div className="flex items-center justify-between"><Ghost onClick={() => go("mode")}>{t.prev}</Ghost><Primary onClick={() => go("p1")} data-testid="connais-setup-next">{t.setupStart}</Primary></div>
          </div>
        );
      case "p1":
        return <FillSheet t={t} lang={lang} name={n1} otherName={n2} answers={p1Answers} onChange={chg(setP1Answers)} onPrev={() => go("setup")} onNext={() => go("handoff")} nextLabel={t.toPartner2} />;
      case "handoff":
        return (
          <div className="animate-fade-up text-center">
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
              <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.handoffTitle(n2)}</h2>
              <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-8">{t.handoffText(n2)}</p>
              <Primary onClick={() => go("p2")} data-testid="connais-handoff">{t.handoffBtn(n2)}</Primary>
            </div>
          </div>
        );
      case "p2":
        return <FillSheet t={t} lang={lang} name={n2} otherName={n1} answers={p2Answers} onChange={chg(setP2Answers)} onPrev={() => go("p1")} onNext={finishSingle} nextLabel={t.seeCompare} />;
      case "td_role":
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.modeTwo}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <button onClick={() => go("td_create")} data-testid="connais-td-create" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all"><h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.createTitle}</h3><p className="text-sm text-[var(--ink-soft)]">{t.createDesc}</p></button>
              <button onClick={() => go("td_join")} data-testid="connais-td-join" className="text-left rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 hover:shadow-md transition-all"><h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.joinTitle}</h3><p className="text-sm text-[var(--ink-soft)]">{t.joinDesc}</p></button>
            </div>
            <button onClick={() => go("td_resume")} data-testid="connais-td-resume" className="mt-5 w-full text-left rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-5 hover:border-[var(--gold)] transition-all">
              <h3 className="font-serif-title text-xl text-[var(--gold)] mb-1">{t.resumeTitle}</h3>
              <p className="text-sm text-[var(--ink-soft)]">{t.resumeDesc}</p>
            </button>
            <div className="mt-8"><Ghost onClick={() => go("mode")}>{t.prev}</Ghost></div>
          </div>
        );
      case "td_resume":
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.resumeTitle}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6">
              <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.codeLabel}</label>
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="connais-resume-code" placeholder={t.codePh} maxLength={6} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm tracking-[0.3em] font-mono uppercase" />
            </div>
            <div className="flex items-center justify-between"><Ghost onClick={() => go("td_role")}>{t.prev}</Ghost><Primary onClick={resumeSeance} data-testid="connais-resume-submit">{t.resumeCta}</Primary></div>
          </div>
        );
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.createTitle}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6"><label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.yourName}</label><input value={myName} onChange={(e) => setMyName(e.target.value)} data-testid="connais-td-name" className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm" /></div>
            <div className="flex items-center justify-between"><Ghost onClick={() => go("td_role")}>{t.prev}</Ghost><Primary onClick={createSeance} data-testid="connais-create-submit">{t.genCode}</Primary></div>
          </div>
        );
      case "td_code": {
        const joinUrl = `${window.location.origin}${patientMode ? "/p/connais" : "/connais"}?code=${code}`;
        return (
          <div className="animate-fade-up text-center">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-4">{t.yourCode}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 mb-6">
              <p className="text-sm text-[var(--ink-soft)] mb-3">{t.shareCode}</p>
              <div className="font-mono text-5xl tracking-[0.4em] text-[var(--gold)] font-bold my-4" data-testid="connais-code-value">{code}</div>
              <div className="flex justify-center my-4"><div className="bg-white p-3 rounded-2xl border border-[var(--border)]"><QRCodeSVG value={joinUrl} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" /></div></div>
              <button onClick={() => navigator.clipboard.writeText(code).then(() => toast.success(t.codeCopied)).catch(() => {})} data-testid="connais-code-copy" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)]">{t.copyCode}</button>
            </div>
            <Primary onClick={() => go("td_fill")} data-testid="connais-code-fill">{t.fillSheet}</Primary>
          </div>
        );
      }
      case "td_join":
        return (
          <div className="animate-fade-up">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.joinTitle}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 space-y-5">
              <div><label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.codeLabel}</label><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="connais-join-code" placeholder={t.codePh} maxLength={6} className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm tracking-[0.3em] font-mono uppercase" /></div>
              <div><label className="block text-sm font-semibold text-[var(--ink)] mb-2">{t.yourName}</label><input value={myName} onChange={(e) => setMyName(e.target.value)} data-testid="connais-join-name" className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm" /></div>
            </div>
            <div className="flex items-center justify-between"><Ghost onClick={() => go("td_role")}>{t.prev}</Ghost><Primary onClick={joinSeance} data-testid="connais-join-submit">{t.join}</Primary></div>
          </div>
        );
      case "td_fill":
        return <FillSheet t={t} lang={lang} name={fallback(myName, t.you)} otherName={t.partner.toLowerCase()} answers={myAnswers} onChange={chg(setMyAnswers)} onPrev={() => go(role === "p1" ? "td_code" : "td_join")} onNext={submitTwo} nextLabel={t.validateSheet} />;
      case "td_wait":
        return <TdWait t={t} code={code} role={role} onReady={(d) => { setResult({ a: d.p1, b: d.p2 }); go("compare"); }} />;
      default:
        return <Compare t={t} lang={lang} a={central.a} b={central.b} marks={marks} setMark={setMark} extra={extra} setExtra={setExtra} onRestart={restart} />;
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-4 no-print">
        {patientMode ? <span /> : <Link to="/" data-testid="connais-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.allModules}</Link>}
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      {render()}
    </Layout>
  );
}

const TdWait = ({ t, code, role, onReady }) => {
  const [status, setStatus] = useState({ p1_submitted: false, p2_submitted: false });
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const d = await axios.get(`${API}/connais/seances/${code}`).then((r) => r.data);
        if (!active) return;
        setStatus(d);
        if (d.both_submitted) { active = false; onReady(d); }
      } catch { /* réseau */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { active = false; clearInterval(id); };
  }, [code, onReady]);
  const mine = role === "p1" ? status.p1_submitted : status.p2_submitted;
  const other = role === "p1" ? status.p2_submitted : status.p1_submitted;
  return (
    <div className="animate-fade-up text-center" data-testid="connais-wait">
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 sm:p-12">
        <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-4">{t.waitTitle}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-md mx-auto mb-6">{t.waitText}</p>
        <p className="text-sm text-[var(--ink-soft)] mb-2">{t.codeLabel} : <span className="font-mono tracking-widest text-[var(--gold)]">{code}</span></p>
        <div className="inline-flex gap-4 text-sm">
          <span className={mine ? "text-[var(--gold)]" : "text-[var(--ink-soft)]"}>{t.you} : {mine ? t.done : "…"}</span>
          <span className={other ? "text-[var(--gold)]" : "text-[var(--ink-soft)]"}>{t.partner} : {other ? t.done : t.inProgress}</span>
        </div>
      </div>
    </div>
  );
};
